import classnames from 'classnames'
import { useTranslatable } from 'src/hooks/locale/utils'
import Loader from './Loader'

interface Props {
  /** Whether at least one mutating request is still in flight. */
  readonly isPending: boolean
  readonly children: React.ReactNode
  readonly className?: string
}

/**
 * Dims its content and blocks interaction with it while a mutating request is being
 * processed, showing a centered notice on top.
 */
export default function PendingRequestOverlay({ isPending, children, className }: Props): JSX.Element {
  const t = useTranslatable()

  return (
    <div className={classnames('relative z-0', className)}>
      <div
        className={classnames('duration-300 ease-in-out', {
          'pointer-events-none select-none opacity-40': isPending,
        })}
        aria-hidden={isPending}
      >
        {children}
      </div>
      {isPending && (
        <div
          className="absolute inset-0 z-10 flex items-start justify-center"
          role="status"
          aria-live="polite"
          data-testid="pendingRequestOverlay"
        >
          <div className="sticky top-24 flex h-fit max-w-full items-center rounded-card bg-lightBlue px-6 py-4 shadow-s">
            <Loader className="size-[28px] min-w-[28px]" />
            <span className="ml-4 text-title font-medium text-primaryTextColor">
              {t('choice:request_sent_processing')}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
